import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL } from "../../config/apiBase";
import { getAuthUserHeader } from "../../utils/userRoles";
import AdminShell from "./AdminShell";
import { startStudentView } from "../../utils/studentView";

const API = API_BASE_URL;

function titleize(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? String(value) : dt.toLocaleString();
}

function isActiveAccount(value) {
  return value === true || value === 1 || ["1", "true", "yes", "active"].includes(String(value || "").trim().toLowerCase());
}

function badgeStyle(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };
  const palette = {
    green: { background: "rgba(76, 175, 80, 0.15)", color: "#9ee6a2", borderColor: "rgba(76, 175, 80, 0.35)" },
    amber: { background: "rgba(255, 193, 7, 0.12)", color: "#ffd977", borderColor: "rgba(255, 193, 7, 0.35)" },
    red: { background: "rgba(244, 67, 54, 0.12)", color: "#ffb1aa", borderColor: "rgba(244, 67, 54, 0.35)" },
    slate: { background: "rgba(148, 163, 184, 0.12)", color: "#d5d9e2", borderColor: "rgba(148, 163, 184, 0.25)" },
  };
  return { ...base, ...(palette[kind] || palette.slate) };
}

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentAdminId = String(localStorage.getItem("student_id") || "").trim().toUpperCase();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState(["admin", "tutor", "student", "tester"]);
  const [form, setForm] = useState({ name: "", email: "", role: "student", is_active: true });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [passwordForm, setPasswordForm] = useState({ new_password: "", confirm_password: "" });
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    async function loadUser() {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}`, {
          cache: "no-store",
          headers: { "X-User": getAuthUserHeader() },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error((data && data.detail) || "Failed to load user");
        if (!active || !mountedRef.current) return;
        const loadedUser = data?.user ? { ...data.user, is_active: isActiveAccount(data.user.is_active) } : null;
        setUser(loadedUser);
        setRoles(Array.isArray(data?.roles) && data.roles.length ? data.roles : ["admin", "tutor", "student", "tester"]);
        setForm({
          name: loadedUser?.name || "",
          email: loadedUser?.email || "",
          role: loadedUser?.role || "student",
          is_active: Boolean(loadedUser?.is_active),
        });
      } catch (err) {
        if (!active || !mountedRef.current) return;
        setError(err?.message || "Failed to load user");
        setUser(null);
      } finally {
        if (active && mountedRef.current) setLoading(false);
      }
    }
    void loadUser();
    return () => {
      active = false;
    };
  }, [userId]);

  const isStudent = String(user?.role || "").toLowerCase() === "student";
  const canResetProgress = isStudent;
  const isSelf = currentAdminId === String(user?.student_id || "").toUpperCase();
  const canDelete = !isSelf && !user?.is_active && String(user?.role || "").toLowerCase() !== "admin";
  const activeLabel = user?.is_active ? "Active" : "Inactive";
  const statusTone = user?.is_active ? "green" : "slate";

  const studentSummary = useMemo(
    () => [
      ["Programme", user?.programme],
      ["Current week", user?.current_week],
      ["Current lesson", user?.current_lesson],
      ["Enrollment date", user?.enrollment_date ? formatDate(user.enrollment_date) : "—"],
      ["Coupon used", user?.coupon_used || "—"],
    ],
    [user]
  );

  const run = async (kind, fn) => {
    if (action || saving) return;
    try {
      setAction(kind);
      await fn();
      setMessage(`${titleize(kind)} complete.`);
      setError("");
      await reload();
    } catch (err) {
      setError(err?.message || `${titleize(kind)} failed`);
    } finally {
      setAction("");
    }
  };

  const reload = async () => {
    const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}`, {
      cache: "no-store",
      headers: { "X-User": getAuthUserHeader() },
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || "Failed to refresh user");
    const loadedUser = data?.user ? { ...data.user, is_active: isActiveAccount(data.user.is_active) } : null;
    setUser(loadedUser);
    setRoles(Array.isArray(data?.roles) && data.roles.length ? data.roles : roles);
    setForm({
      name: loadedUser?.name || "",
      email: loadedUser?.email || "",
      role: loadedUser?.role || "student",
      is_active: Boolean(loadedUser?.is_active),
    });
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to save user");
      setMessage("User updated.");
      setError("");
      await reload();
    } catch (err) {
      setError(err?.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = () => {
    setPasswordError("");
    setPasswordForm({ new_password: "", confirm_password: "" });
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (saving) return;
    setShowPasswordModal(false);
    setPasswordError("");
  };

  const submitPasswordReset = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError("Passwords do not match.");
      return;
    }
    try {
      setSaving(true);
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify(passwordForm),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to reset password");
      setShowPasswordModal(false);
      setMessage("Password reset.");
      await reload();
    } catch (err) {
      setPasswordError(err?.message || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    await run(form.is_active ? "deactivate" : "activate", async () => {
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/${form.is_active ? "deactivate" : "activate"}`, {
        method: "POST",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to update status");
    });
  };

  const resendWelcome = async () => {
    await run("resend welcome email", async () => {
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/resend-welcome`, {
        method: "POST",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to resend welcome email");
    });
  };

  const resetProgress = async () => {
    await run("reset progress", async () => {
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/reset-progress`, {
        method: "POST",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to reset progress");
    });
  };

  const resetCurrentLesson = async () => {
    await run("reset current lesson", async () => {
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/reset-current-lesson`, {
        method: "POST",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to reset current lesson");
    });
  };

  const openDeleteModal = async () => {
    if (!canDelete || user?.is_active || action) return;
    try {
      setAction("preview delete");
      setDeleteError("");
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/delete-all-data/preview`, {
        headers: { "X-User": getAuthUserHeader() },
        cache: "no-store",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to preview deletion");
      setDeletePreview(data);
      setDeleteConfirmation("");
      setShowDeleteModal(true);
    } catch (err) {
      setDeleteError(err?.message || "Failed to preview deletion");
    } finally {
      setAction("");
    }
  };

  const closeDeleteModal = () => {
    if (action === "delete user") return;
    setShowDeleteModal(false);
    setDeletePreview(null);
    setDeleteConfirmation("");
    setDeleteError("");
  };

  const deleteUser = async (event) => {
    event.preventDefault();
    if (!deletePreview || deleteConfirmation.trim().toUpperCase() !== String(user?.student_id || "").toUpperCase()) return;
    try {
      setAction("delete user");
      const res = await fetch(`${API}/api/admin/users/${encodeURIComponent(userId)}/delete-all-data`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-User": getAuthUserHeader() },
        body: JSON.stringify({ student_id: deleteConfirmation.trim() }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to delete user");
      setShowDeleteModal(false);
      navigate("/admin/users", { state: { message: `User ${data.deleted_student_id} and all linked data were permanently deleted.` } });
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete user");
    } finally {
      setAction("");
    }
  };

  async function viewAsStudent() {
    try {
      const view = await startStudentView(user.student_id, `/admin/users/${encodeURIComponent(userId)}`);
      navigate(`/student/dashboard/${encodeURIComponent(view.student_id)}/lessons`);
    } catch (e) { setError(e.message); }
  }

  return (
    <AdminShell title="User Detail" subtitle="Edit account state and linked student data.">
      {loading ? <div className="td-loading">Loading user…</div> : null}
      {error ? <div className="td-empty" style={{ color: "#ffb1aa" }}>{error}</div> : null}
      {message ? <div className="td-empty" style={{ color: "#9ee6a2" }}>{message}</div> : null}

      {!loading && !error && user ? (
        <>
          <section className="td-card reveal" style={{ "--delay": ".08s", padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              <span style={badgeStyle(statusTone)}>{activeLabel}</span>
              <span style={badgeStyle("slate")}>{titleize(user.role)}</span>
              {user.can_hard_delete ? <span style={badgeStyle("green")}>Safe test delete</span> : null}
            </div>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              {[
                ["User ID", user.user_id],
                ["Student ID", user.student_id],
                ["Email", user.email || "—"],
                ["Authentication method", titleize(user.auth_method || "password")],
                ["Linked student record", user.linked_student_record ? "Linked" : "Not linked"],
                ["Last login", formatDate(user.last_login)],
                ["Created at", formatDate(user.created_at)],
              ].map(([label, value]) => (
                <div key={label} style={boxStyle}>
                  <div style={labelStyle}>{label}</div>
                  <div style={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>{value || "—"}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="td-card reveal" style={{ "--delay": ".1s", padding: 16, marginBottom: 16 }}>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <label style={fieldWrap}>
                <span style={labelStyle}>Name</span>
                <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} style={inputStyle} />
              </label>
              <label style={fieldWrap}>
                <span style={labelStyle}>Email</span>
                <input value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} style={inputStyle} />
              </label>
              <label style={fieldWrap}>
                <span style={labelStyle}>Role</span>
                <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} style={inputStyle}>
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {titleize(role)}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ ...fieldWrap, display: "flex", flexDirection: "row", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                />
                <span style={labelStyle}>Active account</span>
              </label>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
              {String(user.role).toLowerCase() === "student" && isActiveAccount(user.is_active) ? (
                <button type="button" onClick={viewAsStudent} style={primaryButton}>View as Student</button>
              ) : null}
              <button type="button" onClick={saveChanges} disabled={saving} style={primaryButton}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button type="button" onClick={toggleActive} disabled={action === "activate" || action === "deactivate"} style={secondaryButton}>
                {form.is_active ? "Deactivate Account" : "Activate Account"}
              </button>
              <button type="button" onClick={openPasswordModal} disabled={saving} style={secondaryButton}>
                Set New Password
              </button>
              <button type="button" onClick={resendWelcome} disabled={action === "resend welcome email"} style={secondaryButton}>
                {action === "resend welcome email" ? "Sending..." : "Resend Welcome Email"}
              </button>
              {canResetProgress ? (
                <>
                  <button type="button" onClick={resetCurrentLesson} disabled={action === "reset current lesson"} style={secondaryButton}>
                    {action === "reset current lesson" ? "Resetting..." : "Reset Current Lesson"}
                  </button>
                  <button type="button" onClick={resetProgress} disabled={action === "reset progress"} style={secondaryButton}>
                    {action === "reset progress" ? "Resetting..." : "Reset Progress"}
                  </button>
                </>
              ) : null}
              {canDelete ? (
                <button type="button" onClick={openDeleteModal} disabled={Boolean(action)} style={dangerButton}>
                  {action === "preview delete" ? "Preparing..." : "Delete User and All Data"}
                </button>
              ) : null}
            </div>

            {!canDelete ? (
              <div style={{ marginTop: 12, color: "var(--muted, #b8b3aa)" }}>
                {isSelf ? "You cannot delete your own account." : user?.is_active ? "Deactivate this account before permanently deleting it." : "Admin accounts cannot be deleted."}
              </div>
            ) : null}
          </section>

          {isStudent ? (
            <section className="td-card reveal" style={{ "--delay": ".12s", padding: 16 }}>
              <div style={{ marginBottom: 12, fontWeight: 800 }}>Student details</div>
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                {studentSummary.map(([label, value]) => (
                  <div key={label} style={boxStyle}>
                    <div style={labelStyle}>{label}</div>
                    <div style={{ fontWeight: 600 }}>{value || "—"}</div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      {showPasswordModal ? (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="password-reset-title">
          <section style={modalCardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
              <div>
                <h2 id="password-reset-title" style={{ margin: 0, fontSize: 24 }}>Set New Password</h2>
                <p style={{ margin: "6px 0 0", color: "var(--muted, #b8b3aa)" }}>Reset this user&apos;s password without needing the old one.</p>
              </div>
              <button type="button" onClick={closePasswordModal} style={buttonStyle}>
                Close
              </button>
            </div>
            <form onSubmit={submitPasswordReset} style={{ display: "grid", gap: 12, marginTop: 18 }}>
              <label style={fieldWrap}>
                <span style={labelStyle}>New Password</span>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(event) => setPasswordForm({ ...passwordForm, new_password: event.target.value })}
                  style={inputStyle}
                  required
                />
              </label>
              <label style={fieldWrap}>
                <span style={labelStyle}>Confirm New Password</span>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(event) => setPasswordForm({ ...passwordForm, confirm_password: event.target.value })}
                  style={inputStyle}
                  required
                />
              </label>
              {passwordError ? <div style={errorBoxStyle}>{passwordError}</div> : null}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="button" onClick={closePasswordModal} style={buttonStyle} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" style={primaryButton} disabled={saving}>
                  {saving ? "Saving..." : "Set Password"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {showDeleteModal ? (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="delete-user-title">
          <section style={modalCardStyle}>
            <h2 id="delete-user-title" style={{ margin: 0, fontSize: 24, color: "#ffb1aa" }}>Permanently Delete User?</h2>
            <p style={{ color: "var(--muted, #b8b3aa)" }}>
              This will permanently delete the user account and all associated student data. This action cannot be undone.
            </p>
            <div style={{ display: "grid", gap: 8, margin: "16px 0" }}>
              {["Name", "User ID", "Student ID", "Email"].map((label) => {
                const values = { Name: user?.name, "User ID": user?.user_id, "Student ID": user?.student_id, Email: user?.email || "—" };
                return <div key={label}><strong>{label}:</strong> {values[label] || "—"}</div>;
              })}
            </div>
            <div style={{ ...boxStyle, marginBottom: 16 }}>
              <strong>Deletion preview</strong>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {Object.entries(deletePreview?.deleted_counts || {}).map(([table, count]) => (
                  <span key={table} style={badgeStyle(count ? "red" : "slate")}>{titleize(table)}: {count}</span>
                ))}
              </div>
            </div>
            <form onSubmit={deleteUser} style={{ display: "grid", gap: 12 }}>
              <label style={fieldWrap}>
                <span style={labelStyle}>Type the exact Student ID to confirm</span>
                <input value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} style={inputStyle} autoComplete="off" required />
              </label>
              {deleteError ? <div style={errorBoxStyle}>{deleteError}</div> : null}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" onClick={closeDeleteModal} style={buttonStyle} disabled={action === "delete user"}>Cancel</button>
                <button type="submit" style={dangerButton} disabled={action === "delete user" || deleteConfirmation.trim().toUpperCase() !== String(user?.student_id || "").toUpperCase()}>
                  {action === "delete user" ? "Deleting..." : "Permanently Delete User and All Data"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </AdminShell>
  );
}

const boxStyle = {
  padding: 12,
  borderRadius: 12,
  background: "rgba(255,255,255,.03)",
  border: "1px solid rgba(255,255,255,.06)",
};

const labelStyle = {
  fontSize: 12,
  color: "var(--muted, #b8b3aa)",
  marginBottom: 6,
};

const fieldWrap = {
  display: "grid",
  gap: 6,
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(8,10,14,.65)",
  color: "#e7e3dc",
};

const buttonStyle = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(255,255,255,.03)",
  color: "#fff",
  cursor: "pointer",
};

const primaryButton = {
  ...buttonStyle,
  background: "rgba(201,165,106,.18)",
};

const secondaryButton = {
  ...primaryButton,
  background: "rgba(255,255,255,.03)",
};

const dangerButton = {
  ...primaryButton,
  background: "rgba(244, 67, 54, .16)",
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.55)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 50,
};

const modalCardStyle = {
  width: "min(720px, 100%)",
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 24,
  padding: 20,
  background: "rgba(12, 15, 20, 0.98)",
  border: "1px solid rgba(255,255,255,.12)",
  boxShadow: "0 24px 70px rgba(0,0,0,.45)",
};

const errorBoxStyle = {
  padding: 12,
  borderRadius: 12,
  border: "1px solid rgba(244,67,54,.35)",
  background: "rgba(244,67,54,.12)",
  color: "#ffb1aa",
};
